/**
 * <LiveChatChannels> — les fils du chat du live, une puce chacun (molecule).
 *
 * Une puce par tuteur du roster — sa capsule et son nom — et « Tous » dans un cours partagé, comme
 * les vignettes de l'ancien chat (`common/liveMode/LiveChat.js`, `chatMembers`) ; vu du masteur, une
 * puce par élève (`student` : son visage, sans capsule). Les puces
 * arrivent dans leur ordre (le tuteur de la séance d'abord) et ne bougent pas quand on en touche une :
 * l'ancien chat remontait le fil ouvert en tête, la puce fuyait sous le doigt.
 *
 * Chaque puce porte le compte de ses messages non lus à son coin, comme la vignette de l'ancien
 * chat ; la puce ouverte ne le porte pas, son fil est en train d'être lu (le même choix que
 * `<TutorRow>`). Le compte se dit au lecteur d'écran dans le libellé de la puce (« Tous, 2 messages
 * non lus »), et la rangée est un groupe de choix (`radiogroup`). Une puce fait 31 px : sa zone de
 * toucher s'étend à 44 en hauteur (`hitSlop`), sans changer sa forme ni déborder sur la voisine.
 *
 * Vue du masteur, la puce d'un élève dit aussi, comme l'avatar de l'ancien chat (`LiveAvatarM`), s'il est
 * dans la salle (`student.online` : un point vert, sinon gris, au pied de son visage) et s'il a levé la main
 * (`student.handRaised` : une main posée sur le visage, en haut à droite, au-dessus du point — là où
 * l'ancien avatar la posait). Main et point tiennent dans la puce : rien ne déborde vers la voisine, dont le
 * compte de non-lus déborde, lui, de son coin. Le lecteur d'écran les lit
 * dans le libellé. Sans ces champs (défaut, le chat de l'élève), la puce est celle d'avant.
 *
 * La rangée défile quand elle déborde (`<HorizontalList>`), retraitée assez pour que le compte qui
 * déborde du coin d'une puce ne soit pas rogné. Elle n'a pas de squelette : elle se dérive du relevé
 * de la séance, déjà là quand le chat s'ouvre ; c'est le fil d'une puce touchée qui charge (le
 * squelette du fil, `<ConversationThread loading>`).
 *
 * Contrôlé : les puces et la puce ouverte arrivent en props, le toucher repart.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { tutorShortName } from "../../../ports/primary/ui/formatters/liveSessionFormatter"
import { type UILiveChatChannel, type UILiveChatChannelOption } from "../../../ports/primary/ui/types/studentLive"
import { Avatar } from "../../atoms/Avatar"
import { Badge } from "../../atoms/Badge"
import { CHOICE_CHIP_HEIGHT, CHOICE_CHIP_LEADING_SIZE, ChoiceChip } from "../../atoms/ChoiceChip/ChoiceChip"
import { HorizontalList } from "../../atoms/HorizontalList"
import { IconBubble } from "../../atoms/icons/IconBubble"
import { NotificationDot } from "../../atoms/NotificationDot"
import { cappedCount } from "../../utils/cappedCount"
import { slopTo } from "../../utils/touchTarget"

export type LiveChatChannelsProps = {
    /** Les puces, dans leur ordre. */
    channels: UILiveChatChannelOption[]
    /** La puce ouverte. */
    selected: UILiveChatChannel
    onSelect: (channel: UILiveChatChannel) => void
    testID?: string
}

const TEXTS = {
    group: "Tous",
    unread: (count: number) => (count === 1 ? "1 message non lu" : `${count} messages non lus`),
    online: "dans la salle",
    offline: "pas dans la salle",
    hand: "main levée",
}

/** Le point de présence, au pied du visage : il tient dans la puce, liseré compris. */
const PRESENCE_DOT_SIZE = 8
/**
 * La main levée, sur le visage : la proportion de l'ancien avatar (une main de 14 px sur un visage de 50,
 * `LiveAvatarM`), lisible à 12 px sur son disque. Elle tient au-dessus du point, sans le toucher.
 */
const HAND_SIZE = 12
const HAND_GLYPH_SIZE = 8

/**
 * La cible de toucher du livre (44 px) : la puce s'étend jusque-là, en hauteur seulement. En largeur, elle
 * fait déjà plus de 44 px, et les puces ne sont qu'à 8 px l'une de l'autre : une zone étendue sur les
 * côtés chevaucherait la voisine.
 */
const CHANNEL_VERTICAL_SLOP = slopTo(CHOICE_CHIP_HEIGHT)
const CHANNEL_HIT_SLOP = { top: CHANNEL_VERTICAL_SLOP, bottom: CHANNEL_VERTICAL_SLOP }

/** Le nom d'une puce : le tuteur (« Camille R. »), l'élève vu du masteur (« Léa »), ou « Tous ». */
export function liveChatChannelLabel(channel: UILiveChatChannelOption): string {
    if (channel.tutor != null) return tutorShortName(channel.tutor)
    return channel.student != null ? channel.student.firstname : TEXTS.group
}

function ChannelChip(props: { channel: UILiveChatChannelOption; selected: boolean; onPress: () => void }) {
    const { channel, selected, onPress } = props
    const label = liveChatChannelLabel(channel)
    const unread = selected ? 0 : (channel.unread ?? 0)
    const online = channel.tutor == null ? channel.student?.online : undefined
    const hand = channel.tutor == null && channel.student?.handRaised === true
    const avatar =
        channel.tutor != null ? (
            <Avatar uri={channel.tutor.avatarUrl} name={label} size={CHOICE_CHIP_LEADING_SIZE} variant="tutor" />
        ) : channel.student != null ? (
            // Un élève n'a pas de capsule : son visage, ou son initiale.
            <Avatar uri={channel.student.avatarUrl} name={label} size={CHOICE_CHIP_LEADING_SIZE} />
        ) : (
            <IconBubble iconName="users" size={CHOICE_CHIP_LEADING_SIZE} />
        )
    // Sans présence ni main dites, le visage seul : la puce d'avant, au pixel près.
    const face =
        online == null && !hand ? (
            avatar
        ) : (
            <View>
                {avatar}
                {online != null && (
                    <View style={styles.presence} pointerEvents="none">
                        <NotificationDot color={online ? "success" : "text-muted"} size={PRESENCE_DOT_SIZE} />
                    </View>
                )}
                {hand && (
                    <View style={styles.hand} pointerEvents="none">
                        <IconBubble
                            iconName="hand-paper"
                            size={HAND_SIZE}
                            iconSize={HAND_GLYPH_SIZE}
                            color="accent"
                            iconColor="text-on-accent"
                        />
                    </View>
                )}
            </View>
        )
    const spoken = [
        label,
        online == null ? null : online ? TEXTS.online : TEXTS.offline,
        hand ? TEXTS.hand : null,
        unread > 0 ? TEXTS.unread(unread) : null,
    ]
        .filter(Boolean)
        .join(", ")
    return (
        <View>
            <ChoiceChip
                label={label}
                selected={selected}
                onPress={onPress}
                leading={face}
                accessibilityLabel={spoken}
                hitSlop={CHANNEL_HIT_SLOP}
            />
            {unread > 0 && (
                <View
                    style={styles.unread}
                    pointerEvents="none"
                    accessibilityElementsHidden
                    importantForAccessibility="no-hide-descendants"
                >
                    <Badge text={cappedCount(unread)} variant="danger" />
                </View>
            )}
        </View>
    )
}

export function LiveChatChannels(props: LiveChatChannelsProps) {
    const { channels, selected, onSelect, testID } = props
    return (
        <View style={styles.root} testID={testID} accessibilityRole="radiogroup">
            <HorizontalList gap="2" padding="2">
                {channels.map((channel) => (
                    <ChannelChip
                        key={channel.id}
                        channel={channel}
                        selected={channel.id === selected}
                        onPress={() => onSelect(channel.id)}
                    />
                ))}
            </HorizontalList>
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    /** Avec le retrait de la rangée (`padding="2"`), les puces s'alignent sur l'en-tête (`4`). */
    root: {
        paddingHorizontal: theme.spacing["2"],
    },
    /** Le compte déborde du coin de la puce, comme celui d'un bouton (`<BadgedIconButton>`). */
    unread: {
        position: "absolute",
        top: -theme.spacing["2"],
        right: -theme.spacing["2"],
    },
    /**
     * La main levée en haut à droite du visage, comme dans l'ancien avatar (`LiveAvatarM` : au-dessus de la
     * pastille, au bord droit) : dans la puce, loin du compte de la puce voisine.
     */
    hand: {
        position: "absolute",
        top: -1,
        right: -1,
    },
    /** Le point de présence au pied du visage, comme la pastille de l'ancien avatar. */
    presence: {
        position: "absolute",
        right: -1,
        bottom: -1,
    },
}))
